import {
  certificatesAtom,
  docAppendicesAtom,
  dropdownVesselsAtom,
  hasSearchedForCertificatesAtom,
} from "../../shared/atoms";
import { log, logE } from "../../shared/services/debug-logger";
import { api, apiUrl, fetchDataWithParams, postData } from "../../shared/services/service";
import { Certificate, DropdownVessel } from "../models/certificates.interfaces";
import { useAtom, useSetAtom } from "jotai";
import { useEffect } from "react";

// Do not move this type to models, this will not be shared with other modules.
type DropdownVesselResponse = {
  NAME: string;
  CODE: string;
  IMO: string;
  TYPE: string;
};

export const useDropdownVessels = () => {
  const [vessels, setVessels] = useAtom(dropdownVesselsAtom);

  useEffect(() => {
    const fetchVessels = async () => {
      log("SERV - fetchVessels");
      try {
        const response = await fetchDataWithParams("listDropDownVessels");
        log("STATE - fetchVessels - apply", { count: response.data.data.length });
        setVessels(
          response.data.data.map((vessel: DropdownVesselResponse) => ({
            vesselName: vessel.NAME,
            code: vessel.CODE,
            imoNumber: vessel.IMO,
            type: vessel.TYPE,
          })),
        );
      } catch (error) {
        logE(error, { operation: "STATE - fetchVessels - failed" });
      }
    };
    fetchVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Vessels are fetched only once on mount
  }, []);

  return { vessels };
};

type CertificateResponse = {
  data: Array<{
    DOC_NAME: string;
    VESSEL_CODE: string;
    VESSEL_NAME: string;
    IMO_NUMBER: string;
    DOC_TYPE: "Certificates" | "Questionnaires" | "Other documents";
    SOURCE: string;
    STATUS: string;
    EXPIRES_ON: string | null;
    NEXT_SURVEY_DATE: string | null;
    USER_NAME: string | null;
    FILE_NAME: string | null;
    REQUESTED_BY: string | null;
    S3_PATH: string | null;
    PORT_OF_ISSUE_IS_WHO_APPROVED: boolean | null;
    APPENDIX_FOR: string | null;
  }>;
};

export const useCertificates = () => {
  const [certificates, setCertificates] = useAtom(certificatesAtom);
  const setHasSearchedForCertificates = useSetAtom(hasSearchedForCertificatesAtom);

  const searchDocuments = async (vessel: DropdownVessel, selectedDocTypes: string[]) => {
    log("SERV - searchDocuments", { vesselCode: vessel.code });
    try {
      const response = await api.get<CertificateResponse>(`${apiUrl}/documents`, {
        params: {
          vessel_name: vessel.vesselName,
          vessel_code: vessel.code,
          imo_number: vessel.imoNumber,
          doc_types: selectedDocTypes,
        },
      });
      log("STATE - searchDocuments - apply", { count: response.data.data.length });
      setCertificates(
        response.data.data.map((cert) => ({
          certificate: {
            docType: cert.DOC_TYPE,
            docName: cert.DOC_NAME,
            source: cert.SOURCE,
            expiresOn: cert.EXPIRES_ON ? new Date(cert.EXPIRES_ON) : null,
            nextSurveyDate: cert.NEXT_SURVEY_DATE ? new Date(cert.NEXT_SURVEY_DATE) : null,
            vesselCode: cert.VESSEL_CODE,
            vesselName: cert.VESSEL_NAME,
            imoNumber: cert.IMO_NUMBER,
            fileName: cert.FILE_NAME ?? "",
            // TODO: Change vessel master email (improve types, we should not share the Certificate type for every use-case)
            vesselMasterEmail: cert.USER_NAME ?? "",
            s3Path: cert.S3_PATH,
            portOfIssueIsWhoApproved: cert.PORT_OF_ISSUE_IS_WHO_APPROVED ?? false,
          } satisfies Certificate,
          isChecked: false,
        })),
      );
      setHasSearchedForCertificates(true);
    } catch (error) {
      logE(new Error("SERV - searchDocuments - failed"));
    }
  };

  return { certificates, searchDocuments };
};

export const useDocAppendices = () => {
  const [docAppendices, setDocAppendices] = useAtom(docAppendicesAtom);
  const setHasSearchedForCertificates = useSetAtom(hasSearchedForCertificatesAtom);

  const searchDocAppendices = async (vessel: DropdownVessel) => {
    try {
      const response = await api.get<CertificateResponse>(`${apiUrl}/documents`, {
        params: {
          vessel_name: vessel.vesselName,
          vessel_code: vessel.code,
          imo_number: vessel.imoNumber,
          doc_types: ["Appendix"],
        },
      });
      setDocAppendices(
        response.data.data.map(
          (cert) =>
            ({
              docType: cert.DOC_TYPE,
              docName: cert.DOC_NAME,
              source: cert.SOURCE,
              expiresOn: cert.EXPIRES_ON ? new Date(cert.EXPIRES_ON) : null,
              nextSurveyDate: cert.NEXT_SURVEY_DATE ? new Date(cert.NEXT_SURVEY_DATE) : null,
              vesselCode: cert.VESSEL_CODE,
              vesselName: cert.VESSEL_NAME,
              imoNumber: cert.IMO_NUMBER,
              fileName: cert.FILE_NAME ?? "",
              // TODO: Change vessel master email (improve types, we should not share the Certificate type for every use-case)
              vesselMasterEmail: cert.USER_NAME ?? "",
              s3Path: cert.S3_PATH,
              portOfIssueIsWhoApproved: cert.PORT_OF_ISSUE_IS_WHO_APPROVED ?? false,
              appendixFor: cert.APPENDIX_FOR,
            }) satisfies Certificate,
        ),
      );
      log("STATE - searchDocAppendices - apply", { count: response.data.data.length });
      setHasSearchedForCertificates(true);
    } catch (error) {
      logE(new Error("SERV - searchDocAppendices - failed"));
    }
  };

  return { docAppendices, searchDocAppendices };
};

export function checkIsSameVessel(vesselA: DropdownVessel | undefined, vesselB: DropdownVessel | undefined) {
  return (
    vesselA?.vesselName === vesselB?.vesselName &&
    vesselA?.code === vesselB?.code &&
    vesselA?.imoNumber === vesselB?.imoNumber
  );
}

// One or many, they get attached one by one.
// Attaching one large zip might fail due to size limits.
export async function sendCertificatesByEmail(certificates: Certificate[], receiverEmail: string) {
  log("SERV - sendCertificatesByEmail", { count: certificates.length });
  try {
    await postData(`send-certificates-by-email`, {
      certificates,
      receiverEmail,
    });
    log("SERV - sendCertificatesByEmail - completed");
  } catch (error) {
    logE(new Error("SERV - sendCertificatesByEmail - failed"));
    throw error;
  }
}
